import { useMemo } from 'react';
import { isManagedObject, isSystemObject } from '../../lib/objectsThatMatter';
import type { AutomationByObject, AutomationTech, ObjectsResult } from '../../types/org.types';

// "Objects with the most automation" (TYR-234), on a stored client scan only.
// Every number comes from the stored bundle passed in, never the live store.
// Managed-package items are not counted and system objects never show.
// Approvals and sharing rules arrived in v2.6: an older scan shows
// "not captured" in those cells, never a zero.
type Row = {
  object: string;
  label: string;
  total: number;
  counts: Partial<Record<AutomationTech, number>>;
  sharingRules: number | null;
  records: number | null;
};

const COLUMNS: { tech: AutomationTech; label: string }[] = [
  { tech: 'Flow', label: 'Flows' },
  { tech: 'Process Builder', label: 'Process Builders' },
  { tech: 'Workflow Rule', label: 'Workflow rules' },
  { tech: 'Apex Trigger', label: 'Triggers' },
  { tech: 'Validation Rule', label: 'Validation rules' },
  { tech: 'Approval Process', label: 'Approvals' },
];

export function AutomationTop10Card({
  byObject,
  objects,
  sharingRuleCounts,
  approvalsCaptured,
  onOpen,
}: {
  byObject: AutomationByObject | undefined;
  objects: ObjectsResult | null | undefined;
  // undefined or null = the scan did not capture sharing rules.
  sharingRuleCounts: Record<string, number> | null | undefined;
  approvalsCaptured: boolean;
  onOpen: (apiName: string) => void;
}) {
  const rows = useMemo<Row[]>(() => {
    if (!byObject) return [];
    const recordsByObject = new Map((objects?.objects ?? []).map((o) => [o.apiName, o.recordCount ?? null]));
    const out: Row[] = [];
    for (const g of byObject.groups) {
      if (g.object === null || isSystemObject(g.object) || isManagedObject(g.object)) continue;
      const own = g.items.filter((i) => !i.managed);
      if (own.length === 0) continue;
      const counts: Partial<Record<AutomationTech, number>> = {};
      for (const i of own) counts[i.type] = (counts[i.type] ?? 0) + 1;
      out.push({
        object: g.object,
        label: g.label,
        total: own.length,
        counts,
        sharingRules: sharingRuleCounts ? (sharingRuleCounts[g.object] ?? 0) : null,
        records: recordsByObject.get(g.object) ?? null,
      });
    }
    return out.sort((a, b) => b.total - a.total || (b.records ?? -1) - (a.records ?? -1)).slice(0, 10);
  }, [byObject, objects, sharingRuleCounts]);

  if (!byObject) return null;

  const notCaptured = <span className="text-[11px] text-neutral-600">not captured</span>;
  const cell = 'py-1.5 px-2 text-right tabular-nums text-neutral-400';

  return (
    <div className="border border-border bg-surface-2 p-4 mb-6">
      <div className="text-[11px] uppercase tracking-widest text-neutral-500">Objects with the most automation</div>
      <p className="text-xs text-neutral-500 mt-0.5 mb-3">
        Top 10 by total automation, ties broken on records. Managed-package items are not counted. Click a row to
        open that object.
      </p>
      {rows.length === 0 ? (
        <div className="text-sm text-neutral-500">No automation on any object in this scan.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                <th className="text-left py-1.5 pr-2 font-medium">Object</th>
                <th className="text-right py-1.5 px-2 font-medium">Automations</th>
                {COLUMNS.map((c) => (
                  <th key={c.tech} className="text-right py-1.5 px-2 font-medium">
                    {c.label}
                  </th>
                ))}
                <th className="text-right py-1.5 px-2 font-medium">Sharing rules</th>
                <th className="text-right py-1.5 pl-2 font-medium">Records</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.object}
                  onClick={() => onOpen(r.object)}
                  className="border-t border-border cursor-pointer hover:bg-surface-hover"
                >
                  <td className="py-1.5 pr-2">
                    <div className="text-crux-light font-semibold">{r.label}</div>
                    <div className="text-[11px] font-mono text-neutral-600">{r.object}</div>
                  </td>
                  <td className={`${cell} font-semibold text-crux-light`}>{r.total}</td>
                  {COLUMNS.map((c) => (
                    <td key={c.tech} className={cell}>
                      {c.tech === 'Approval Process' && !approvalsCaptured ? notCaptured : (r.counts[c.tech] ?? 0)}
                    </td>
                  ))}
                  <td className={cell}>{r.sharingRules ?? notCaptured}</td>
                  <td className={cell}>{r.records?.toLocaleString() ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
